import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholderImage from './../../assets/placeholder.png';
import infoIcon from './../../assets/ic_info_i.svg';
import searchIcon from './../../assets/ic_search.svg';
import { normalizeCdnUrl } from '../../services/CdnUtils';
import ApiService from '../../services/ApiService';
import './FramePopup.css';

const FramePopup = ({
    availableFrames = [],
    selectedFrame,
    allFrames = [],
    handleFrameSelect,
    closePopup,
    readonly = false,
    nickname = ''
}) => {
    const navigate = useNavigate();
    const [collectionOnly, setCollectionOnly] = useState(true);
    const [query, setQuery] = useState('');
    const [detailsMessage, setDetailsMessage] = useState(null);

    const sourceFrames = readonly || collectionOnly ? availableFrames : allFrames;
    const selectedFrameId = selectedFrame?.id ?? selectedFrame;
    const visibleFrames = useMemo(() => {
        const normalizedQuery = query.trim().toLocaleLowerCase('ru');
        if (!normalizedQuery) return sourceFrames;
        return sourceFrames.filter((frame) => `${frame.title || ''} ${frame.desc || ''}`.toLocaleLowerCase('ru').includes(normalizedQuery));
    }, [query, sourceFrames]);

    const showFrameDetails = async (frame) => {
        if (frame.animeId == null) {
            setDetailsMessage(`${frame.title}: специальная рамка, которая не выдаётся за просмотр конкретного аниме.`);
            return;
        }

        try {
            const response = await ApiService.checkFrames(frame.id);
            const responseMessage = typeof response?.data === 'string'
                ? response.data
                : response?.data?.message;
            setDetailsMessage(responseMessage || frame.desc || 'Информация о рамке недоступна.');
        } catch (error) {
            console.error('Error checking frame:', error);
            setDetailsMessage(frame.desc || 'Не удалось загрузить информацию о рамке.');
        }
    };

    const openFrame = (frame) => {
        if (readonly) {
            showFrameDetails(frame);
            return;
        }
        if (collectionOnly) {
            handleFrameSelect?.(frame);
            return;
        }
        if (frame.animeId != null) navigate(`/anime/${frame.animeId}`);
        else showFrameDetails(frame);
    };

    return (
        <div className="frame-popup-container" onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePopup?.();
        }}>
            <section className="frame-popup" role="dialog" aria-modal="true" aria-labelledby="frame-popup-title">
                <header className="frame-popup__header">
                    <div>
                        <h2 id="frame-popup-title">Рамки</h2>
                        <p>{readonly && nickname ? `Коллекция ${nickname}` : 'Выберите оформление аватара'}</p>
                    </div>
                    <button className="ui-close" type="button" onClick={closePopup} aria-label="Закрыть">
                        ×
                    </button>
                </header>

                <div className="frame-popup__toolbar">
                    {!readonly ? (
                        <div className="frame-popup__tabs" role="tablist" aria-label="Фильтр рамок">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={collectionOnly}
                                className={collectionOnly ? 'is-active' : ''}
                                onClick={() => setCollectionOnly(true)}
                            >
                                Мои <span>{availableFrames.length}</span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={!collectionOnly}
                                className={!collectionOnly ? 'is-active' : ''}
                                onClick={() => setCollectionOnly(false)}
                            >
                                Все <span>{allFrames.length}</span>
                            </button>
                        </div>
                    ) : <strong className="frame-popup__count">{availableFrames.length} в коллекции</strong>}

                    <label className="frame-popup__search">
                        <img src={searchIcon} alt="" aria-hidden="true" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Найти рамку"
                            aria-label="Найти рамку"
                        />
                    </label>
                </div>

                <div className="frames-container">
                    {visibleFrames.length > 0 ? visibleFrames.map((frame) => (
                        <article key={frame.id} className={`frame-card${String(selectedFrameId) === String(frame.id) ? ' selected-frame' : ''}`}>
                            <button className="frame-card__main" type="button" onClick={() => openFrame(frame)}>
                                <span className="frame-card__preview">
                                    <img
                                        src={frame.url ? normalizeCdnUrl(frame.url) : placeholderImage}
                                        alt=""
                                        className="frame-image"
                                    />
                                </span>
                                <span className="frame-info">
                                    <strong>{frame.title || 'Без названия'}</strong>
                                    <small>{frame.desc || (collectionOnly || readonly ? 'Рамка из коллекции' : 'Открыть аниме')}</small>
                                </span>
                            </button>
                            <button
                                className="check-frame-button"
                                type="button"
                                onClick={() => showFrameDetails(frame)}
                                aria-label={`Подробнее о рамке ${frame.title || ''}`}
                            >
                                <img src={infoIcon} alt="" />
                            </button>
                        </article>
                    )) : (
                        <div className="frame-popup__empty">
                            <span aria-hidden="true">◇</span>
                            <p>{sourceFrames.length > 0 ? 'Рамки не найдены' : 'В коллекции пока нет рамок'}</p>
                        </div>
                    )}
                </div>
            </section>

            {detailsMessage ? (
                <div className="frame-details-overlay" onMouseDown={(event) => {
                    if (event.target === event.currentTarget) setDetailsMessage(null);
                }}>
                    <section className="frame-details" role="alertdialog" aria-modal="true" aria-labelledby="frame-details-title">
                        <div className="frame-details__icon" aria-hidden="true">i</div>
                        <h3 id="frame-details-title">Как получить рамку</h3>
                        <p>{detailsMessage}</p>
                        <button type="button" onClick={() => setDetailsMessage(null)}>Понятно</button>
                    </section>
                </div>
            ) : null}
        </div>
    );
};

export default FramePopup;
